import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { ChevronsUpDown, X } from "lucide-react";
import { cn } from "../lib/utils";
import ClientLogo from "./ClientLogo";

/**
 * Pick a client out of the database instead of typing their name.
 *
 * Typing was how the catalogue drifted: «VD Group» / «VDGroup», «Tomin Family
 * Development» / «TominFamilyDevelopment» — the same company spelled two ways,
 * each becoming its own row, with the money on one and the work on the other.
 * A picker writes `client_id`, so a work is attached to the record rather than
 * to a string that happens to look like it.
 *
 * A 250-row <Select> is unusable, hence a filtered list. `freeText` keeps the
 * name a legacy work already carries visible even when nothing is linked, so
 * editing an old entry never silently erases what it said.
 */
export default function ClientSelect({
  clients = [],
  valueId,
  valueName,
  onChange,
  placeholder = "Pick a client...",
  disabled,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const selected = useMemo(
    () => clients.find((c) => c.id === valueId) || null,
    [clients, valueId]
  );

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const pool = needle
      ? clients.filter(
          (c) =>
            (c.name || "").toLowerCase().includes(needle) ||
            (c.country || "").toLowerCase().includes(needle)
        )
      : clients;
    return pool.slice(0, 80);
  }, [clients, q]);

  // An unlinked legacy name still deserves to be shown — greyed, so it reads as
  // "text we inherited", not as a real link.
  const label = selected?.name || valueName || "";

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
        <PopoverTrigger asChild disabled={disabled}>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              "flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border border-zinc-700 bg-zinc-900/60 px-2 text-left text-sm",
              "hover:border-zinc-600 disabled:opacity-50"
            )}
          >
            {selected ? (
              <ClientLogo
                src={selected.logo_url}
                variant={selected.logo_variant}
                name={selected.name}
                className="h-6 w-8"
                iconClassName="h-3 w-3"
              />
            ) : null}
            <span
              className={cn(
                "min-w-0 flex-1 truncate",
                selected ? "text-zinc-100" : label ? "text-zinc-500 italic" : "text-zinc-600"
              )}
              title={label || placeholder}
            >
              {label || placeholder}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0 bg-zinc-900 border-zinc-700" align="start">
          <div className="border-b border-zinc-800 p-2">
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or country..."
              className="h-8 bg-zinc-800 border-zinc-700 text-sm text-zinc-50"
            />
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {matches.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-zinc-500">
                Nothing matches. Add the client on /clients first.
              </p>
            ) : (
              matches.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onChange?.({ id: c.id, name: c.name });
                    setOpen(false);
                    setQ("");
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-zinc-800",
                    c.id === valueId ? "text-amber-300" : "text-zinc-200"
                  )}
                >
                  <ClientLogo
                    src={c.logo_url}
                    variant={c.logo_variant}
                    name={c.name}
                    className="h-6 w-8"
                    iconClassName="h-3 w-3"
                  />
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                  {c.country ? (
                    <span className="shrink-0 text-[10px] text-zinc-500">{c.country}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
      {label && !disabled ? (
        <button
          type="button"
          title="Clear"
          onClick={() => onChange?.({ id: "", name: "" })}
          className="shrink-0 rounded p-1 text-zinc-600 hover:text-red-400"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}
